// A minimal client-side path router — no react-router dependency.
//
// B1 was a single view; B2 adds two more screens (the claim flow + the device
// manager), so the shell needs to branch on `location.pathname`. Rather than pull
// in a routing library for three routes, this is a ~40-line hook over the History
// API: it reports the current path + query, navigates via `pushState` (no full
// reload — auth is the Owner Session cookie, ADR-0060, so a client-side transition
// needs no re-auth round-trip), and re-renders on `popstate` (Back/
// Forward). Keeping it tiny + framework-agnostic-shaped also keeps the shell a
// thin layer (ADR-0021). The gallery stays the home route ("/").
//
// The Worker that hosts this SPA (Workers Static Assets,
// `not_found_handling: "single-page-application"` in cloud/wrangler.jsonc) serves
// index.html for every non-API path, so a deep-link to `/claim?code=…` (the
// camera's QR target) loads the SPA, and this hook then reads the path + `?code=`
// from `window.location`.

import { useCallback, useEffect, useState } from "react";

/** The current route: the pathname plus a parsed query-string accessor. */
export interface Route {
  /** `window.location.pathname`, e.g. "/", "/claim", "/devices". */
  path: string;
  /** The parsed `?…` query of the current URL. */
  query: URLSearchParams;
  /** Push a new path (optionally with a query) without a full reload. */
  navigate: (to: string) => void;
}

/**
 * Prefix-aware active-route match for nav highlighting. The home route ("/") matches
 * only exactly; any other target matches itself or a nested child ("/devices/abc"), so
 * future nested paths highlight their section instead of going dark (and `/claim` can be
 * recognised as its own section). Distinct from the screen dispatch, which stays exact.
 */
export function isActivePath(current: string, target: string): boolean {
  if (target === "/") return current === "/";
  return current === target || current.startsWith(target + "/");
}

/** Read the live location into a `{ path, query }` snapshot. */
function readLocation(): { path: string; query: URLSearchParams } {
  return {
    path: window.location.pathname,
    query: new URLSearchParams(window.location.search),
  };
}

/**
 * Subscribe to the current route. Re-renders on Back/Forward (`popstate`) and on
 * in-app `navigate()` calls. `navigate` is a no-op reload-free `pushState` — auth
 * rides the Owner Session cookie (ADR-0060) on each API call, so a client-side
 * transition needs no re-auth.
 */
export function useRoute(): Route {
  const [loc, setLoc] = useState(readLocation);

  useEffect(() => {
    const onPop = (): void => setLoc(readLocation());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    // Same-origin path push; ignore a navigate to the current URL (no churn).
    if (to === window.location.pathname + window.location.search) return;
    window.history.pushState(null, "", to);
    setLoc(readLocation());
  }, []);

  return { path: loc.path, query: loc.query, navigate };
}
